import { containerStatusPresentation } from './container-status-presentation.mjs?v=20260806.100';

function escapeHtml(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

export const INSPECTION_SECTIONS = Object.freeze([
  { key: 'progress', label: 'Progress' },
  { key: 'identity', label: 'Container' },
  { key: 'customer', label: 'Customer', adminOnly: true },
  { key: 'transport', label: 'Transport' },
  { key: 'dates', label: 'Dates' },
  { key: 'finance', label: 'Finance' },
]);

const GROUP_KEYS = Object.freeze({
  progress: ['status', 'ocean_status', 'pre_alert_status', 'current_stage', 'current_leg', 'next_action'],
  identity: ['customer_name', 'container_no', 'master_bl_no', 'size_type', 'weight_lbs', 'ssl', 'terminal_name', 'hold_note'],
  customer: [],
  transport: [
    'pin', 'chassis_no', 'empty_container_no', 'pickup_company', 'pod_count',
    'delivery_warehouse_address', 'transfer_from_address', 'transfer_to_address',
    'pickup_driver_name', 'return_driver_name', 'transfer_driver_name',
  ],
  dates: [
    'created_at', 'eta_at', 'lfd_at', 'pickup_appointment_at', 'delivery_appointment_at',
    'transfer_at', 'return_empty_lfd_at', 'empty_returned_at', 'updated_at',
  ],
  finance: [
    'ctf_tmf_status', 'pickup_cost_cents', 'return_cost_cents', 'transfer_cost_cents',
    'exception_fee_cents', 'special_fee_amount_cents', 'customer_special_fee_cents', 'service_fee_cents',
    'customer_quote_cents', 'customer_receivable_cents', 'total_operational_cost_cents', 'profit_cents',
    'remittance_status', 'special_fee_name', 'special_fee_detail', 'customer_special_fee_detail',
  ],
});

function valueTone(key, value) {
  if (key === 'status' || key === 'ocean_status') {
    return containerStatusPresentation(value).tone;
  }
  const normalized = String(value || '').toLowerCase();
  if (key === 'profit_cents') return 'profit';
  if (key === 'customer_special_fee_cents' || key === 'service_fee_cents') return 'customer-revenue';
  if (key.includes('special_fee') || key === 'exception_fee_cents') return 'special-fee';
  if (normalized.includes('empty returned') || normalized === 'paid' || normalized === 'received') return 'success';
  if (normalized.includes('pending') || normalized.includes('arrived') || normalized === 'unpaid') return 'danger';
  if (normalized.includes('available')) return 'available';
  if (normalized.includes('delivered')) return 'delivered';
  return 'neutral';
}

function progressRail(row, formatValue) {
  const milestones = [
    ['Task', true],
    ['Port', Boolean(row.eta_at || row.ocean_status)],
    ['Loaded', Boolean(row.loaded_delivered_at || row.status === 'loaded_delivered')],
    ['Empty', Boolean(row.empty_ready_at || row.empty_picked_up_at)],
    ['Returned', Boolean(row.is_empty_returned || row.empty_returned_at)],
  ];
  const active = Math.max(0, milestones.reduce((last, [, done], index) => (done ? index : last), 0));
  return `<div class="inspection-progress" style="--inspection-progress:${active / (milestones.length - 1)}">
    <div class="inspection-progress__truck" aria-hidden="true"><svg viewBox="0 0 28 18"><path d="M2 4h14v9H2zM16 7h6l4 4v2H16z"/><circle cx="7" cy="14" r="2"/><circle cx="21" cy="14" r="2"/></svg></div>
    <div class="inspection-progress__line" aria-hidden="true"><i></i></div>
    <ol>${milestones.map(([label, done], index) => `<li class="${done ? 'is-complete' : ''}${index === active ? ' is-current' : ''}"><i></i><span>${escapeHtml(label)}</span></li>`).join('')}</ol>
    <p>${escapeHtml(formatValue('next_action', row) || formatValue('status', row) || '')}</p>
  </div>`;
}

const RELATED_LABELS = Object.freeze({
  account_number: 'Account Number',
  name: 'Name',
  address: 'Address',
  delivery_notes: 'Delivery Notes',
  phone: 'Phone',
  wechat: 'WeChat',
  truck: 'Truck',
  driver_code: 'Driver Number',
  real_email: 'Real Email',
  account_login_email: 'Account Login Email',
  note: 'Note',
});

function relatedCard(title, record) {
  const entries = Object.entries(record || {}).filter(([key, value]) => (
    key !== 'id' && value !== '' && value !== null && value !== undefined
  ));
  if (!entries.length) return '';
  return `<article class="inspection-related-card">
    <h3>${escapeHtml(title)}</h3>
    <dl>${entries.map(([key, value]) => `<div><dt>${escapeHtml(RELATED_LABELS[key] || key)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}</dl>
  </article>`;
}

function relatedMarkup(section, related) {
  if (!related) return '';
  if (section === 'customer' && related.customer) {
    return `<div class="inspection-related">${relatedCard('Customer', related.customer)}</div>`;
  }
  if (section === 'identity' && related.customer) {
    return `<div class="inspection-related">${relatedCard('Customer', related.customer)}</div>`;
  }
  if (section === 'transport' && Array.isArray(related.drivers) && related.drivers.length) {
    return `<div class="inspection-related">${related.drivers.map((driver, index) => (
      relatedCard(driver.name || `Driver ${index + 1}`, driver)
    )).join('')}</div>`;
  }
  return '';
}

function inspectionSectionsForRole(role) {
  return INSPECTION_SECTIONS.filter((section) => !section.adminOnly || role === 'admin');
}

export function inspectionRoutes(containerId, role) {
  const encoded = encodeURIComponent(String(containerId || ''));
  return inspectionSectionsForRole(role).map(({ key }) => `#/inspect/${encoded}~${key}`);
}

export function parseInspectionParam(param, role) {
  const [encodedId, section = 'progress'] = String(param || '').split('~');
  const id = decodeURIComponent(encodedId || '');
  if (!id || !inspectionSectionsForRole(role).some((item) => item.key === section)) return null;
  return { id, section };
}

export function containerInspectionMarkup({ row, section, role, columns = [], related = null, formatValue = () => '' } = {}) {
  if (!row?.id) throw new TypeError('Container inspection row is required.');
  const sectionInfo = inspectionSectionsForRole(role).find((item) => item.key === section);
  if (!sectionInfo) throw new TypeError('Container inspection section is invalid.');
  const allowedColumns = new Map(columns.map((column) => [column.key, column]));
  const keys = GROUP_KEYS[section].filter((key) => allowedColumns.has(key));
  const values = keys.map((key) => {
    const column = allowedColumns.get(key);
    const formatted = formatValue(key, row);
    const empty = formatted === '' || formatted === null || formatted === undefined;
    return `<div class="inspection-value inspection-value--${valueTone(key, row[key] ?? formatted)}">
      <dt>${escapeHtml(column.label)}</dt><dd>${escapeHtml(empty ? '—' : formatted)}</dd>
    </div>`;
  }).join('');
  const relatedContent = relatedMarkup(section, related);
  const body = section === 'customer'
    ? relatedContent || '<p class="container-inspection__empty">Customer information is not available.</p>'
    : `<dl class="container-inspection__values">${values || '<div class="inspection-value"><dt>Information</dt><dd>Not available for this role</dd></div>'}</dl>${relatedContent}`;
  return `<span data-workspace-container-number>${escapeHtml(row.container_no || 'Container')} · ${escapeHtml(sectionInfo.label)}</span>
    <section class="container-inspection" data-inspection-section="${escapeHtml(section)}" aria-label="${escapeHtml(sectionInfo.label)} for ${escapeHtml(row.container_no || 'Container')}">
      ${section === 'progress' ? progressRail(row, formatValue) : ''}
      ${body}
    </section>`;
}
